import type { RaiserLevel } from '@pams/shared';
import { RAISER_LEVEL_LABEL } from '@/lib/labels';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C5c §10 · amended P7A §2] AT WHICH LEVEL WAS THIS ORDER RAISED? — `HQ` · `DRO` · `Unit`, beside
 *  the order's name on the arrangements list, the Assign PA page, the PA record, the preview and the
 *  fill sheet. Formerly `HqPill`, which could say one word because the field was a boolean.
 *
 *  The server decides it (`raisedAtLevel`, the RAISER's position); this only draws it.
 *
 *  ⚠️ `null` — AND ANY VALUE THIS BUILD DOES NOT KNOW — DRAWS NOTHING, never a blank lozenge and never
 *  a throw. `null` is the server saying the raiser's unit does not resolve; an unknown value is an
 *  API newer than this bundle (or older, sending no key at all, which reads `undefined` at run time
 *  whatever the type says). `Object.hasOwn` is the test because a plain index would hand back
 *  `undefined.word` for the second and `Object.prototype`'s members for a value like `toString`.
 *
 *  ⚠️ A NEUTRAL LOZENGE, NOT `StatusPill`. That component is the ONE primary state of a detail
 *  record and carries a status tone; this is a fact about who raised the order and has no tone. The
 *  classes are the Inbox's capped-rows notice's and the PA-number chip's, so the three read as one
 *  family of muted facts beside a name.
 *
 *  ⚠️ IT IS STILL A BADGE IN A LIST ROW, which DESIGN_LANGUAGE §4's dot-versus-pill rule warns
 *  against — the client asked for a pill by name (§10a, and again in P7A §2 for every level). What
 *  keeps it from becoming the "wall of colour" that rule is about is that it carries NO HUE: status
 *  stays the only thing on the row with colour, and the three levels differ by WORD alone. Recorded
 *  as a departure from the rule's letter, not its reason. ⚠️ And since P7A every order carries one.
 *
 *  ⚠️ A NATIVE `title`, NEVER A TOOLTIP — one per row would be [D4.4]'s measured overlay-per-row
 *  cost, and a Tooltip is a new `census.spec.ts` entry for a sentence that needs no structure.
 *
 *  ⚠️ `raiser-pill` + `data-level` IS THE TEST HANDLE. The id was `hq-pill`; a pill reading `DRO`
 *  under that name would lie, so it was renamed and the level put on an attribute a test can read.
 *
 *  ⚠️ NO BREAKPOINT OF ANY KIND — it is one word that never wraps; the caller's slot decides where
 *  it sits, and a `shrink-0` there is what keeps the NAME giving way rather than the pill.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const RaiserPill = ({ level }: { level: RaiserLevel | null }) => {
  if (level === null || !Object.hasOwn(RAISER_LEVEL_LABEL, level)) return null;
  const label = RAISER_LEVEL_LABEL[level];
  return (
    <span
      data-testid="raiser-pill"
      data-level={level}
      title={label.title}
      className="whitespace-nowrap rounded bg-secondary px-1.5 py-0.5 text-xs text-muted-foreground"
    >
      {label.word}
    </span>
  );
};
